import type { JourneyState, VisitorAnswer } from '@/domain/types';

/**
 * Journey persistence.
 *
 * Answers live in this browser and nowhere else. There is no account, no
 * server-side session and no id: refreshing the page or losing signal
 * mid-question should not cost someone their progress, and that is the entire
 * reason this file exists.
 *
 * The data is cleared automatically once a submission succeeds, and there is a
 * visible "erase my answers" control on the result page.
 */

const STORAGE_KEY_PREFIX = 'awc.journey.';

/**
 * Bump when the shape of JourneyState changes; older saves are then discarded.
 *
 * Also bump when the question set itself changes. Version 3 dropped the
 * accountability question and added home-and-animals: a part-finished journey
 * saved under version 2 holds an answer to a question that no longer exists and
 * is missing one that does, and resuming it would score someone against a
 * questionnaire they never actually saw.
 *
 * Version 4 is the rounds rewrite: seven questions added, `togetherness` and
 * `social-energy` replaced, and the whole list reordered. `currentIndex` in a
 * version 3 save points at a completely different question, so those saves are
 * dropped rather than migrated.
 *
 * Version 5 is the reorder that put `good-saturday` first and `humour` at the
 * end of round one. No question was added or removed, so the answers
 * themselves would still have been readable — but `currentIndex` is a position
 * in the list, and every position moved. A version 4 save would resume
 * somebody in the middle of a round they had already finished, or skip them
 * past children. Dropping the save costs an unfinished journey; migrating it
 * wrong costs a wrong result, so the save goes.
 */
export const JOURNEY_SCHEMA_VERSION = 5;

/** Saved progress older than this is treated as stale and dropped. */
const MAX_AGE_MS = 1000 * 60 * 60 * 24 * 14; // 14 days

function keyFor(profileId: string): string {
  return `${STORAGE_KEY_PREFIX}${profileId}`;
}

function storageAvailable(): boolean {
  try {
    const probe = '__awc_probe__';
    window.localStorage.setItem(probe, probe);
    window.localStorage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
}

/** True when we can persist. Safari private mode and locked-down browsers cannot. */
export const canPersist: boolean =
  typeof window !== 'undefined' && storageAvailable();

export function createJourneyState(profileId: string): JourneyState {
  const now = new Date().toISOString();
  return {
    version: JOURNEY_SCHEMA_VERSION,
    profileId,
    answers: {},
    currentIndex: 0,
    startedAt: now,
    updatedAt: now,
  };
}

function isVisitorAnswer(value: unknown): value is VisitorAnswer {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Partial<VisitorAnswer>;
  if (typeof v.questionId !== 'string') return false;
  switch (v.kind) {
    case 'single':
      return typeof (v as { optionId?: unknown }).optionId === 'string';
    case 'multi':
      return (
        Array.isArray((v as { optionIds?: unknown }).optionIds) &&
        (v as { optionIds: unknown[] }).optionIds.every((id) => typeof id === 'string')
      );
    case 'scale':
      return Number.isFinite((v as { value?: unknown }).value);
    default:
      return false;
  }
}

/**
 * Reads saved progress, discarding anything malformed, stale or from an older
 * schema. Returns null rather than throwing, so a corrupted value can never
 * white-screen the app.
 */
export function loadJourney(profileId: string): JourneyState | null {
  if (!canPersist) return null;
  try {
    const raw = window.localStorage.getItem(keyFor(profileId));
    if (!raw) return null;

    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;

    const state = parsed as Partial<JourneyState>;
    if (state.version !== JOURNEY_SCHEMA_VERSION) return null;
    if (state.profileId !== profileId) return null;
    if (typeof state.updatedAt !== 'string') return null;

    const age = Date.now() - new Date(state.updatedAt).getTime();
    if (!Number.isFinite(age) || age > MAX_AGE_MS) {
      clearJourney(profileId);
      return null;
    }

    const answers: Record<string, VisitorAnswer> = {};
    for (const [questionId, answer] of Object.entries(state.answers ?? {})) {
      if (isVisitorAnswer(answer) && answer.questionId === questionId) {
        answers[questionId] = answer;
      }
    }

    return {
      version: JOURNEY_SCHEMA_VERSION,
      profileId,
      answers,
      currentIndex: Math.max(0, Math.floor(state.currentIndex ?? 0)),
      startedAt: typeof state.startedAt === 'string' ? state.startedAt : state.updatedAt,
      updatedAt: state.updatedAt,
    };
  } catch {
    return null;
  }
}

export function saveJourney(state: JourneyState): void {
  if (!canPersist) return;
  try {
    window.localStorage.setItem(
      keyFor(state.profileId),
      JSON.stringify({ ...state, updatedAt: new Date().toISOString() }),
    );
  } catch {
    // Quota or private mode. Progress simply is not saved; the journey still works.
  }
}

/**
 * Moves the saved cursor without touching the answers.
 *
 * This is what "keep exploring" on the result page needs: someone who has
 * finished a round is sitting on the last question of it, and sending them
 * back to the journey has to land them on the first question they have not
 * answered rather than the last one they have. A no-op where there is no save
 * to move.
 */
export function setCurrentIndex(profileId: string, index: number): void {
  const state = loadJourney(profileId);
  if (!state) return;
  saveJourney({ ...state, currentIndex: Math.max(0, Math.floor(index)) });
}

export function clearJourney(profileId: string): void {
  if (!canPersist) return;
  try {
    window.localStorage.removeItem(keyFor(profileId));
  } catch {
    // Nothing useful to do.
  }
}
